/**
 * "Has every subscription this member holds run out?"
 *
 * One implementation, shared by the My Plans modal and the full-screen gate, so
 * the two can never disagree about whether a plan is still live.
 *
 * The rule, in order:
 *   1. No plans at all              -> not blocked (free / manually-granted member)
 *   2. ANY non-subscription plan    -> not blocked, and stop looking
 *      A one-time purchase or an admin grant is lifetime. Blocking those would
 *      lock out someone who never subscribed in the first place.
 *   3. Every window closed          -> blocked
 *
 * Cancellation is deliberately NOT a case of its own. Cancelling stops the next
 * renewal and caps the window at the end of the period already paid for — so a
 * cancelled plan is "expired" only once its date has passed, exactly like one
 * that lapsed on a failed debit. The date decides access; the status only
 * decides the wording. This matches the backend, where syncAccessWindow() caps
 * membership_accesses.expires_at at current_period_end rather than revoking.
 */

/**
 * Plan dates arrive already offset to IST; order timestamps arrive naive and are
 * UTC. Normalise both so a comparison is meaningful whatever the browser's zone.
 */
export function toPlanDate(value) {
  const raw = String(value || "").trim().replace(" ", "T");
  if (!raw) return null;
  const iso =
    raw.includes("T") && !/([zZ]|[+-]\d{2}:?\d{2})$/.test(raw) ? `${raw}Z` : raw;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Render a plan date in IST, so it matches the vendor dashboard and gateway. */
export function formatPlanDate(
  value,
  opts = { day: "numeric", month: "short", year: "numeric" },
) {
  const d = toPlanDate(value);
  return d ? d.toLocaleDateString(undefined, { timeZone: "Asia/Kolkata", ...opts }) : "";
}

/**
 * Has this plan's paid-up window closed?
 *
 * A MISSING date reads as NOT ended — a subscription whose first charge webhook
 * has not landed yet (or one still in trial) has no period recorded, and locking
 * someone out over absent data is the one failure this must never produce.
 */
export function planEnded(plan) {
  const sub = plan?.subscription_plan;
  if (sub && sub.has_access === false) {
    return true;
  }
  const end = toPlanDate(sub?.access_until ?? sub?.current_period_end);
  return end !== null && end.getTime() < Date.now();
}

export function planAccessUntil(plan) {
  const sub = plan?.subscription_plan;
  if (sub?.has_access === false) return null;
  return sub?.access_until ?? sub?.current_period_end ?? null;
}

/**
 * Decide whether the whole membership should be gated.
 *
 * @param  {Array|null} plans  rows from fetchMyPlans (null while loading)
 * @return {{blocked: boolean, reason: string, expired: Array}}
 */
export function subscriptionGate(plans) {
  if (!Array.isArray(plans) || plans.length === 0) {
    return { blocked: false, reason: "no-plans", expired: [] };
  }

  // Rule 2 — one lifetime entitlement is enough to keep the doors open.
  if (plans.some((p) => !p.is_subscription)) {
    return { blocked: false, reason: "has-non-subscription", expired: [] };
  }

  const expired = [];
  let live = 0;
  for (const p of plans) {
    if (planEnded(p)) expired.push(p);
    else live += 1;
  }

  if (live > 0) {
    return { blocked: false, reason: "has-live-subscription", expired };
  }

  return { blocked: true, reason: "all-subscriptions-expired", expired };
}

/**
 * How a plan's gateway status should READ to a member.
 *
 * Shared because the list and the gate were labelling the same row from two
 * private maps and disagreeing: a `paused` plan showed "paused" in My Plans and
 * "Expired" on the gate, because the gate's map handled only canceled/past_due
 * and let everything else fall through to "Expired".
 */
const STATUS_LABELS = {
  active: "Active",
  trialing: "Trial",
  pending: "Pending",
  paused: "Paused",
  canceled: "Cancelled",
  cancelled: "Cancelled",
  expired: "Expired",
  completed: "Completed",
  past_due: "Payment failed",
  refunded: "Refunded",
};

/**
 * Statuses that describe a plan still running. Accurate in a list; NOT on the
 * expiry gate, where the window has demonstrably closed — a row still marked
 * `active` there means a renewal never landed, and calling it "Active" on a
 * screen that says access has ended would be nonsense.
 */
const RUNNING_STATUSES = new Set(["active", "trialing", "pending", ""]);

/** The plan's status as a member should see it. Unknown values pass through. */
export function planInTrial(plan) {
  return plan?.subscription_plan?.is_in_trial === true;
}

export function planTrialEndsAt(plan) {
  return planInTrial(plan)
    ? plan?.subscription_plan?.trial_ends_at ?? plan?.subscription_plan?.next_billing_at ?? null
    : null;
}

export function planStatusLabel(plan) {
  const raw = String(plan?.subscription_status || "").toLowerCase().trim();
  if (planInTrial(plan) && RUNNING_STATUSES.has(raw)) return "Trial";
  if (!raw) return "Subscription";
  return STATUS_LABELS[raw] || raw.replace(/_/g, " ");
}

/** Why a LAPSED plan ended — the same labels, but never a still-running one. */
export function endedReasonLabel(plan) {
  const raw = String(plan?.subscription_status || "").toLowerCase().trim();
  return RUNNING_STATUSES.has(raw) ? "Expired" : planStatusLabel(plan);
}

export function isManualPlan(plan) {
  return plan?.subscription_plan?.billing_mode === "manual";
}

export function planRenewUrl(plan) {
  const sub = plan?.subscription_plan;
  return (isManualPlan(plan) && sub?.can_pay_now && sub?.pay_now_url) || plan?.buy_url || "";
}
